/**
 * MetaManageModal — pausar/ativar e ajustar orçamento na Meta, com prévia e confirmação.
 *
 * Toda alteração passa por duas chamadas: a primeira (sem `confirm`) só devolve o
 * antes/depois lido na Meta; a segunda, depois do clique em "Confirmar", escreve de verdade.
 * O limite por hora e a permissão são do backend: aqui
 * só mostramos a mensagem que ele devolve. A tabela de campanhas só reflete a mudança na
 * próxima coleta.
 */

import { useEffect, useState } from 'react'
import {
  duplicateMetaCampaign, getMetaManageAudit, manageMetaBudget, manageMetaStatus,
} from '../db/api.js'
import {
  BUDGET_LABELS, LEVEL_LABELS, describePreview, nextStatus, parseAmount,
} from '../lib/metaManage.js'

function Shell({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-header"><h3 className="modal-title">{title}</h3></div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">{footer}</div>
      </div>
    </div>
  )
}

function Review({ preview }) {
  const view = describePreview(preview)
  if (!view) return null
  return (
    <div>
      <p className="modal-message"><strong>{view.name}</strong></p>
      <p className="modal-message">{view.from} → {view.to}</p>
      {view.note && <p className="modal-message">{view.note}</p>}
    </div>
  )
}

export function MetaManageModal({ row, level, mode, clientId, onClose }) {
  const autoReview = mode === 'status' || mode === 'duplicate'
  const [step, setStep] = useState(autoReview ? 'loading' : 'input')
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)
  const [kind, setKind] = useState('daily_budget')
  const [amountText, setAmountText] = useState('')
  const [busy, setBusy] = useState(false)

  const base = { clientId, level, entityId: row.id }
  const newStatus = nextStatus(row.status)
  const amount = parseAmount(amountText)

  const call = (confirm) => {
    if (mode === 'duplicate') return duplicateMetaCampaign({ clientId, entityId: row.id, confirm })
    return mode === 'status'
      ? manageMetaStatus({ ...base, status: newStatus, confirm })
      : manageMetaBudget({ ...base, kind, amount, confirm })
  }

  async function review() {
    setBusy(true)
    setError(null)
    try {
      setPreview((await call(false)).preview)
      setStep('review')
    } catch (err) {
      setError(err.message || 'Não foi possível revisar a alteração.')
      setStep(autoReview ? 'error' : 'input')
    } finally {
      setBusy(false)
    }
  }

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      setPreview((await call(true)).preview)
      setStep('done')
    } catch (err) {
      setError(err.message || 'A alteração não foi aplicada.')
      setStep('error')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (autoReview) review()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let title = `Ajustar orçamento da ${LEVEL_LABELS[level]}`
  if (mode === 'duplicate') title = 'Duplicar campanha'
  else if (mode === 'status') title = `${newStatus === 'PAUSED' ? 'Pausar' : 'Ativar'} ${LEVEL_LABELS[level]}`

  let body = null
  let footer = <button className="btn btn-ghost" onClick={onClose}>Fechar</button>
  if (step === 'loading') {
    body = <p className="modal-message">Lendo o estado atual na Meta…</p>
  } else if (step === 'input') {
    body = (
      <div>
        <p className="modal-message"><strong>{row.name}</strong></p>
        <label className="modal-message" htmlFor="meta-budget-kind">Tipo de orçamento</label>
        <select id="meta-budget-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(BUDGET_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <label className="modal-message" htmlFor="meta-budget-amount">Novo valor</label>
        <input
          id="meta-budget-amount" inputMode="decimal" placeholder="Ex.: 120,00" value={amountText}
          onChange={(e) => setAmountText(e.target.value)}
        />
        {error && <p className="modal-message" role="alert">{error}</p>}
      </div>
    )
    footer = (
      <>
        <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
        <button className="btn btn-black" onClick={review} disabled={busy || amount === null}>Revisar</button>
      </>
    )
  } else if (step === 'review') {
    body = (
      <div>
        <Review preview={preview} />
        <p className="modal-message">Esta alteração é aplicada na Meta na hora e fica registrada na auditoria.</p>
      </div>
    )
    footer = (
      <>
        <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
        <button className="btn btn-black" onClick={confirm} disabled={busy}>
          {busy ? 'Aplicando…' : 'Confirmar'}
        </button>
      </>
    )
  } else if (step === 'done') {
    body = (
      <div>
        <p className="modal-message">Alteração aplicada na Meta.</p>
        <Review preview={preview} />
        <p className="modal-message">A tabela mostra o novo estado depois da próxima coleta.</p>
      </div>
    )
  } else if (step === 'error') {
    body = <p className="modal-message" role="alert">{error}</p>
  }

  return <Shell title={title} onClose={onClose} footer={footer}>{body}</Shell>
}

const OUTCOME_LABELS = { success: 'Aplicada', failed: 'Falhou', blocked: 'Bloqueada' }
const when = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

function auditChange(entry) {
  if (entry.action === 'create') {
    const adset = entry.after_state?.adset?.name
    return `Campanha criada pausada: ${entry.after_state?.campaign?.name || entry.entity_name || ''}${adset ? ` · conjunto ${adset}` : ''}`
  }
  if (entry.action === 'duplicate') return `Cópia pausada criada (${entry.after_state?.campaign_id || '—'})`
  const before = entry.before_state || {}
  const after = entry.after_state || {}
  const keys = Object.keys(after)
  return keys.map((k) => `${k}: ${before[k] ?? '—'} → ${after[k]}`).join(' · ')
}

export function MetaAuditModal({ onClose }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    getMetaManageAudit()
      .then((data) => { if (alive) setRows(data) })
      .catch((err) => { if (alive) setError(err.message || 'Não foi possível carregar a auditoria.') })
    return () => { alive = false }
  }, [])

  let body = <p className="modal-message">Carregando…</p>
  if (error) body = <p className="modal-message" role="alert">{error}</p>
  else if (rows && rows.length === 0) body = <p className="modal-message">Nenhuma alteração feita pelo CRM ainda.</p>
  else if (rows) {
    body = (
      <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
        <table style={{ width: '100%' }}>
          <thead><tr><th>Quando</th><th>Quem</th><th>Item</th><th>Alteração</th><th>Resultado</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{when(r.created_at)}</td>
                <td>{r.user_name || '—'}</td>
                <td title={r.entity_id}>{LEVEL_LABELS[r.level]}: {r.entity_name || r.entity_id}</td>
                <td>{auditChange(r)}</td>
                <td title={r.error || undefined}>{OUTCOME_LABELS[r.outcome] || r.outcome}{r.error ? ` — ${r.error}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  return (
    <Shell title="Histórico de alterações" onClose={onClose} footer={<button className="btn btn-ghost" onClick={onClose}>Fechar</button>}>
      {body}
    </Shell>
  )
}
